import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Upload, X, FileText, Loader2, CheckCircle, AlertTriangle, Trophy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "../i18n";
import axios from "axios";

const API = '/api';

export function ImportModal({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await axios.post(`${API}/library/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(res.data);
      if (res.data.imported > 0 && onSuccess) onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail || "Error al importar el archivo";
      setResult({ error: detail });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    setImporting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
        data-testid="import-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.2 }}
          className="bg-background border border-border rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="import-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-base">{t.importModal.title}</h3>
            <Button variant="ghost" size="icon" className="w-8 h-8" onClick={handleClose} data-testid="import-modal-close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-4 flex-1 overflow-y-auto space-y-4">
            {!result ? (
              <>
                <p className="text-sm text-muted-foreground">
                  {t.importModal.description}
                </p>

                {/* Drop zone */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? "border-primary bg-primary/5"
                      : file
                        ? "border-green-500/50 bg-green-500/5"
                        : "border-border hover:border-muted-foreground/40"
                  }`}
                  data-testid="import-drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.json"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                  
                  {file ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-10 h-10 text-green-500" />
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-muted-foreground" />
                      <p className="text-sm font-medium">{t.importModal.dragHere}</p>
                      <p className="text-xs text-muted-foreground">{t.importModal.clickToSelect}</p>
                      <p className="text-xs text-muted-foreground mt-1">CSV, JSON</p>
                    </div>
                  )}
                </div>

                {/* Platform badges */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {["Goodreads", "Letterboxd", "IMDb", "Backloggd", "Trakt"].map((p) => (
                    <span key={p} className="text-xs px-2.5 py-1 bg-muted rounded-full text-muted-foreground">{p}</span>
                  ))}
                </div>

                {/* Import button */}
                <Button
                  className="w-full"
                  disabled={!file || importing}
                  onClick={handleImport}
                  data-testid="import-submit"
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.importModal.importing}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {t.importModal.importButton}
                    </>
                  )}
                </Button>
              </>
            ) : result.error ? (
              /* Error state */
              <div className="text-center py-6 space-y-3" data-testid="import-error">
                <AlertTriangle className="w-12 h-12 mx-auto text-destructive" />
                <h4 className="font-semibold text-lg">{t.importModal.errorTitle}</h4>
                <p className="text-sm text-muted-foreground">{result.error}</p>
                <Button variant="outline" onClick={() => { setResult(null); setFile(null); }}>
                  {t.importModal.tryAgain}
                </Button>
              </div>
            ) : (
              /* Success state */
              <div className="text-center py-4 space-y-4" data-testid="import-success">
                <CheckCircle className="w-14 h-14 mx-auto text-green-500" />
                
                <div>
                  <h4 className="font-bold text-xl">
                    {t.importModal.imported(result.imported)}
                  </h4>
                  {result.platform && (
                    <p className="text-sm text-muted-foreground mt-1 capitalize">
                      {t.importModal.fromPlatform} {result.platform}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-green-500/10 rounded-lg p-3">
                    <p className="text-lg font-bold text-green-500">{result.imported}</p>
                    <p className="text-xs text-muted-foreground">{t.importModal.importedCount}</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-lg font-bold">{result.skipped}</p>
                    <p className="text-xs text-muted-foreground">{t.importModal.duplicates}</p>
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <p className="text-lg font-bold">{result.failed}</p>
                    <p className="text-xs text-muted-foreground">{t.importModal.failed}</p>
                  </div>
                </div>

                {result.new_achievements > 0 && (
                  <div className="bg-primary/10 rounded-lg p-4 flex items-center gap-3" data-testid="import-achievements">
                    <Trophy className="w-8 h-8 text-primary flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-semibold">
                        {t.importModal.achievementsUnlocked(result.new_achievements)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t.importModal.goToAchievements}</p>
                    </div>
                  </div>
                )}

                <Button className="w-full" onClick={handleClose} data-testid="import-done">
                  {t.importModal.done}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
